import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr, pollJob } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { NextActionCard } from "../components/shared/NextActionCard";
import { toast } from "sonner";
import { capture, EVENTS } from "../analytics/posthog";
import { ArrowLeft, Bot, CheckCircle2, Loader2, Sparkles, Coins, FileText, ListChecks, Circle, Target } from "lucide-react";

const STAGES = ["Revisando contexto empresarial", "Identificando dependencias", "Analizando derechos de decisión", "Diseñando excepciones", "Construyendo sistema", "Preparando verificación"];

const SYS_LABEL = {
  GENERATED: "Generado", DRAFT: "Generado", CONFIGURATION_REQUIRED: "Config. requerida", APPROVED: "Aprobado",
  ACTIVE: "En implantación", IN_TEST: "En prueba", VERIFIED: "Verificado",
  NEEDS_ADJUSTMENT: "Necesita ajustes", ARCHIVED: "Archivado",
};
const SYS_TONE = {
  VERIFIED: "text-emerald-400", ACTIVE: "text-emerald-400", IN_TEST: "text-orange-400",
  NEEDS_ADJUSTMENT: "text-orange-400", APPROVED: "text-cyan-400", CONFIGURATION_REQUIRED: "text-amber-400",
  GENERATED: "text-slate-400", DRAFT: "text-slate-400", ARCHIVED: "text-slate-600",
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [deps, setDeps] = useState([]);
  const [targetDep, setTargetDep] = useState("");
  const [running, setRunning] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [blocked, setBlocked] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [planStages, setPlanStages] = useState([]);
  const [planIdx, setPlanIdx] = useState(0);

  const load = async () => {
    try {
      const [{ data: p }, { data: d }] = await Promise.all([api.get(`/projects/${id}`), api.get("/dependencies")]);
      setProject(p); setDeps(d);
      const firstValid = (p.dependency_ids || []).find((did) => d.some((x) => x.id === did));
      if (firstValid) setTargetDep(firstValid);
      if (p.needs_planning && !planning) planProject(p);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, [id]);

  const planProject = async () => {
    setPlanning(true); setPlanIdx(0);
    try {
      const { data } = await api.post(`/projects/${id}/plan-async`);
      if (data.blocked) { setBlocked(data); setPlanning(false); return; }
      setPlanStages(data.stages || []);
      const job = await pollJob(data.job_id, (i) => setPlanIdx(i));
      window.dispatchEvent(new Event("credits:refresh"));
      if (job.status === "DONE") await load();
      else toast.error("Architect no pudo planificar el proyecto. Inténtalo de nuevo.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setPlanning(false); }
  };

  const runArchitect = async () => {
    setRunning(true); setStageIdx(0); setBlocked(null);
    const prevCount = (project.systems || []).length;
    try {
      const { data } = await api.post(`/projects/${id}/architect-async`, { dependency_id: targetDep || null });
      if (data.blocked) { setBlocked(data); setRunning(false); return; }
      capture(EVENTS.buildStarted, { build_type: "architect_build" });
      capture(EVENTS.architectStarted, { build_type: "architect_build" });
      const job = await pollJob(data.job_id, (i) => setStageIdx(i));
      if (job.status === "DONE" && job.result?.system) {
        capture(EVENTS.buildCompleted, { build_type: "architect_build" });
        capture(EVENTS.architectCompleted, { build_type: "architect_build" });
        toast.success(`Sistema generado · −${job.result.credits_consumed} créditos`);
        window.dispatchEvent(new Event("credits:refresh"));
        await load();
      } else {
        const { data: p } = await api.get(`/projects/${id}`);
        if ((p.systems || []).length > prevCount) { setProject(p); window.dispatchEvent(new Event("credits:refresh")); toast.success("Sistema generado"); }
        else toast.error("Architect no pudo completar este paso. Tu trabajo anterior está guardado.");
      }
    } catch (e) {
      toast.error(apiErr(e, "Architect no pudo completar este paso. Tu trabajo anterior está guardado."));
    } finally { setRunning(false); }
  };

  if (!project) return <div className="mono-label text-slate-500">CARGANDO…</div>;

  const ss = project.stage_summary;
  const blockerDeps = (project.blocker_dependency_ids || []).map((bid) => deps.find((x) => x.id === bid)).filter(Boolean);
  const primaryDep = blockerDeps[0] || deps.find((x) => (project.dependency_ids || []).includes(x.id));
  const pendingUnits = (project.work_units || []).filter((u) => u.status === "PENDING").length;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/app/projects")} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Proyectos</button>

      {/* HEADER — objective + dependency + real progress */}
      <div className="card-arch p-5 sm:p-6">
        <div className="halo" />
        <div className="relative">
          <p className="mono-label text-cyan-400/80">Objetivo</p>
          <h1 className="mt-1 font-head text-xl sm:text-2xl font-bold text-white leading-snug">{project.objective}</h1>
          {primaryDep && (
            <p className="mt-3 text-sm text-slate-400 flex items-center gap-2"><Target className="h-4 w-4 text-amber-400 shrink-0" /> Dependencia que reducimos: <span className="text-slate-300">{primaryDep.title}</span></p>
          )}
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="mono-label text-slate-400">Progreso real</span>
              <span className="font-mono text-white" data-testid="project-progress-label">{project.progress}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/5"><div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${project.progress}%` }} /></div>
            {ss && (
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400" data-testid="project-stage-summary">
                <span>{ss.systems_built}/{ss.systems_total} sistemas</span>
                <span>· {ss.implemented} en marcha</span>
                <span>· {ss.verified} verificados</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* NEXT ACTION */}
      {!running && !planning && !blocked && project.next_action && <NextActionCard action={project.next_action} compact />}

      {/* Planning */}
      {planning && (
        <div className="card-arch p-6" data-testid="project-planning-panel">
          <div className="halo-cyan" />
          <div className="relative flex items-center gap-2 text-cyan-400"><Bot className="h-5 w-5" /><span className="mono-label">Architect está planificando</span></div>
          <div className="relative mt-4 space-y-2.5">
            {(planStages.length ? planStages : ["Analizando objetivo…"]).map((s, i) => (
              <div key={s} className="flex items-center gap-3 text-sm">
                {i < planIdx ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : i === planIdx ? <Loader2 className="h-4 w-4 text-amber-400 animate-spin" /> : <span className="h-4 w-4 rounded-full border border-white/15" />}
                <span className={i <= planIdx ? "text-white" : "text-slate-500"}>{s}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Architect run (only relevant when there is pending work) */}
      {!planning && (pendingUnits > 0 || (project.systems || []).length === 0 || blocked) && (
        <div className="card-arch p-5 sm:p-6">
          <div className="halo-cyan" />
          <div className="relative flex items-center gap-2 text-cyan-400"><Bot className="h-5 w-5" /><span className="mono-label">AI Architect</span></div>
          {running ? (
            <div className="relative mt-5 space-y-2.5">
              <div className="flex items-center gap-2 text-amber-400 text-sm"><Sparkles className="h-4 w-4" /> Architect está trabajando…</div>
              {STAGES.map((s, i) => (
                <div key={s} className="flex items-center gap-3 text-sm">
                  {i < stageIdx ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : i === stageIdx ? <Loader2 className="h-4 w-4 text-amber-400 animate-spin" /> : <span className="h-4 w-4 rounded-full border border-white/15" />}
                  <span className={i <= stageIdx ? "text-white" : "text-slate-500"}>{s}</span>
                </div>
              ))}
            </div>
          ) : blocked ? (
            <div className="relative mt-4 rounded-xl border border-red-500/30 bg-red-500/5 p-4">
              <p className="text-sm text-white font-medium">{blocked.message}</p>
              <p className="mt-1 text-sm text-slate-400">Trabajo restante: {blocked.pending_units} sistema(s). Saldo: {blocked.balance} créditos.</p>
              <p className="mt-1 text-xs text-emerald-400/80">Tu progreso está guardado. Al reanudar, Architect continúa por donde se quedó y no repite el trabajo ya cobrado.</p>
              <div className="mt-3 flex flex-col sm:flex-row flex-wrap gap-2">
                <Button data-testid="project-topup-button" onClick={() => navigate("/app/billing")} className="bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold"><Coins className="mr-2 h-4 w-4" /> Comprar créditos</Button>
                <Button data-testid="project-resume-button" onClick={runArchitect} variant="outline" className="bg-white/[0.03] border-white/10 hover:bg-white/[0.07] text-white rounded-full font-semibold"><Sparkles className="mr-2 h-4 w-4" /> Reanudar</Button>
              </div>
            </div>
          ) : (
            <div className="relative mt-4 space-y-3">
              <p className="text-sm text-slate-400">Architect construirá un Delegation System específico para reducir la dependencia seleccionada. Coste: <span className="text-white font-medium">15 créditos</span>.</p>
              {deps.length > 0 && (
                <Select value={targetDep} onValueChange={setTargetDep}>
                  <SelectTrigger data-testid="architect-target-select" className="bg-white/[0.03] border-white/10 w-full sm:max-w-md"><SelectValue placeholder="Siguiente pendiente (automático)" /></SelectTrigger>
                  <SelectContent className="bg-[#0F1117] border-white/10 text-slate-200">
                    {deps.map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button data-testid="architect-run-trigger-button" onClick={runArchitect} className="w-full sm:w-auto bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold"><Sparkles className="mr-2 h-4 w-4" /> Construir con Architect · 15 créditos</Button>
            </div>
          )}
        </div>
      )}

      {/* Work plan checkpoints */}
      {(project.work_units || []).length > 0 && (
        <div className="card-arch p-5 sm:p-6">
          <div className="flex items-center gap-2 text-cyan-400"><ListChecks className="h-4 w-4" /><span className="mono-label">Plan de trabajo</span></div>
          <div className="mt-4 space-y-2.5">
            {project.work_units.map((u) => (
              <div key={u.id} className="flex items-center gap-3 text-sm" data-testid={`work-unit-${u.id}`}>
                {u.status === "COMPLETED" ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" /> : <Circle className="h-4 w-4 text-slate-500 shrink-0" />}
                <span className={u.status === "COMPLETED" ? "text-white" : "text-slate-400"}>{u.title || "Sistema de delegación"}</span>
                <span className="ml-auto text-xs font-mono text-slate-500 shrink-0">{u.status === "COMPLETED" ? "Sistema listo" : "Pendiente"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Generated systems */}
      <div>
        <h2 className="font-head text-lg font-semibold text-white">Sistemas del proyecto</h2>
        <div className="mt-3 space-y-3">
          {(project.systems || []).length === 0 && <p className="text-sm text-slate-500">Aún no hay sistemas. Ejecuta Architect para crear el primero.</p>}
          {(project.systems || []).map((sys) => {
            const im = sys.implementation;
            return (
              <button key={sys.id} data-testid={`system-view-${sys.id}`} onClick={() => navigate(`/app/systems/${sys.id}`)}
                className="w-full text-left card-arch p-5 hover:border-amber-500/30 transition-colors">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0"><FileText className="h-4 w-4 text-cyan-400 shrink-0" /><h4 className="font-medium text-white truncate">{sys.title}</h4></div>
                  <span className={`text-xs font-mono shrink-0 ${SYS_TONE[sys.status] || "text-slate-400"}`}>{SYS_LABEL[sys.status] || sys.status}</span>
                </div>
                {im && ["ACTIVE", "IN_TEST", "NEEDS_ADJUSTMENT"].includes(sys.status) && (
                  <div className="mt-3">
                    <div className="h-1 rounded-full bg-white/5"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${im.total ? (im.done / im.total) * 100 : 0}%` }} /></div>
                    <p className="mt-1 text-xs text-slate-500">Implantación {im.done}/{im.total} hitos</p>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
